import { Box, Typography, useTheme } from '@mui/material'

type StatusType = 'toMove' | 'correctMove' | 'puzzleSolved' | 'puzzleFailed' | 'hintUsed' | 'solution'

interface PuzzleQuestStatusHeaderProps {
    status: StatusType
    playerColor: 'white' | 'black'
}

const SOLUTION_ICON = '/assets/images/puzzle-quest/puzzle-solution.svg'
const HINT_ICON = '/assets/images/puzzle-quest/puzzle-hint.svg'
const FAILED_ICON = '/assets/images/puzzle-quest/puzzle-failed.svg'
const SOLVED_ICON = '/assets/images/puzzle-quest/puzzle-solved.svg'

interface StatusConfig {
    icon?: string
    color: string
    label: (playerColor?: 'white' | 'black') => string
}

const STATUS_CONFIG: Record<Exclude<StatusType, 'toMove' | 'solution'>, StatusConfig> = {
    correctMove: {
        icon: SOLVED_ICON,
        color: '#82c057',
        label: () => 'Correct Move',
    },
    puzzleSolved: {
        icon: SOLVED_ICON,
        color: '#82c057',
        label: () => 'PUZZLE SOLVED',
    },
    puzzleFailed: {
        icon: FAILED_ICON,
        color: '#c05757',
        label: () => 'PUZZLE FAILED',
    },
    hintUsed: {
        icon: HINT_ICON,
        color: '#ff942c',
        label: () => 'HINT USED',
    },
}

const StatusRow = ({ icon, color, label }: { icon?: string; color: string; label: string }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            height: '100%',
        }}
    >
        {icon && (
            <Box
                component="img"
                src={icon}
                alt=""
                sx={{
                    width: { xs: '26px', md: '42px' },
                    height: { xs: '22px', md: '35px' },
                    flexShrink: 0,
                    objectFit: 'contain',
                }}
            />
        )}
        <Typography
            variant="h5"
            sx={{
                fontSize: { xs: '18px', md: '24px' },
                color,
                textTransform: 'uppercase',
                lineHeight: { xs: '24px', md: 'normal' },
            }}
        >
            {label}
        </Typography>
    </Box>
)

export const PuzzleQuestStatusHeader: React.FC<PuzzleQuestStatusHeaderProps> = ({ status, playerColor }) => {
    const theme = useTheme()

    const renderStatus = () => {
        if (status === 'toMove') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <Box
                        sx={{
                            width: { xs: '22px', md: '24px' },
                            height: { xs: '22px', md: '24px' },
                            borderRadius: '6px',
                            background:
                                playerColor === 'white' ? theme.palette.common.white : theme.palette.common.black,
                            border: playerColor === 'black' ? `2px solid ${theme.palette.common.white}` : 'none',
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '18px', md: '24px' },
                            color: theme.palette.common.white,
                            textTransform: 'uppercase',
                            lineHeight: { xs: '24px', md: 'normal' },
                        }}
                    >
                        {playerColor === 'white' ? 'WHITE' : 'BLACK'} TO MOVE
                    </Typography>
                </Box>
            )
        }
        if (status === 'solution') {
            return (
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: '114px', md: '151px' },
                        height: { xs: '24px', md: '29px' },
                    }}
                >
                    <Box
                        component="img"
                        src={SOLUTION_ICON}
                        alt=""
                        sx={{
                            position: 'absolute',
                            left: { xs: 'calc(50% - 46px)', md: 'calc(50% - 61.5px)' },
                            top: '1px',
                            transform: 'translateX(-50%)',
                            width: { xs: '22px', md: '28px' },
                            height: { xs: '22px', md: '28px' },
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: { xs: '27.19%', md: '26.49%' },
                            right: 0,
                            top: 0,
                            fontSize: { xs: '18px', md: '24px' },
                            color: theme.palette.orange.main,
                            textTransform: 'uppercase',
                            lineHeight: { xs: '24px', md: 'normal' },
                        }}
                    >
                        SOLUTION
                    </Typography>
                </Box>
            )
        }
        // All other statuses
        const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]
        return <StatusRow icon={config.icon} color={config.color} label={config.label(playerColor)} />
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: { xs: '24px', md: '35px' },
                minHeight: { xs: '24px', md: '35px' },
                '& > *': {
                    animation: 'zoomIn 0.45s ease-in-out',
                },
                '@keyframes zoomIn': {
                    '0%': {
                        opacity: 0,
                        transform: 'scale(0)',
                    },
                    '60%': {
                        opacity: 1,
                        transform: 'scale(1.15)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                    },
                },
            }}
        >
            {renderStatus()}
        </Box>
    )
}
